/**
 * CADMetadataPanel - Displays geometric metadata of a CAD model
 */

import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Chip,
} from '@mui/material';
import {
  Straighten as DimensionIcon,
  ThreeDRotation as GeometryIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CADMetadata } from '../../types/cad';

interface CADMetadataPanelProps {
  metadata: CADMetadata | null;
  filename?: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

const CADMetadataPanel: React.FC<CADMetadataPanelProps> = ({ metadata }) => {
  const { t } = useTranslation();

  const getConfidenceLabel = (confidence?: string) => {
    const key = confidence === 'high' || confidence === 'medium' || confidence === 'low'
      ? confidence
      : 'low';
    return t(`cad.metadata.confidence.${key}`);
  };

  if (!metadata) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          {t('cad.metadata.empty')}
        </Typography>
      </Box>
    );
  }

  const sections = [
    {
      icon: <DimensionIcon fontSize="small" />,
      title: t('cad.metadata.dimensions'),
      rows: [
        [t('cad.metadata.boundingBox'), `${metadata.boundingBox.x.toFixed(1)} × ${metadata.boundingBox.y.toFixed(1)} × ${metadata.boundingBox.z.toFixed(1)} mm`],
        ...(metadata.volume != null ? [[t('cad.metadata.volume'), `${(metadata.volume / 1000).toFixed(1)} cm³`]] : []),
        ...(metadata.surfaceArea != null ? [[t('cad.metadata.surfaceArea'), `${metadata.surfaceArea.toFixed(1)} mm²`]] : []),
      ],
    },
    {
      icon: <GeometryIcon fontSize="small" />,
      title: t('cad.metadata.geometry'),
      rows: [
        [t('cad.metadata.triangleCount'), formatNumber(metadata.triangleCount)],
        [t('cad.metadata.vertexCount'), formatNumber(metadata.vertexCount)],
        [t('cad.metadata.edgeCount'), formatNumber(metadata.edgeCount)],
        [t('cad.metadata.faceCount'), formatNumber(metadata.faceCount)],
      ],
    },
    {
      icon: <FileIcon fontSize="small" />,
      title: t('cad.metadata.file'),
      rows: [
        [t('cad.metadata.originalFormat'), metadata.originalFormat.toUpperCase()],
        [t('cad.metadata.originalFileSize'), formatBytes(metadata.originalFileSizeBytes)],
        [t('cad.metadata.glbSize'), formatBytes(metadata.glbFileSizeBytes)],
      ],
    },
  ];

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {t('cad.metadata.title')}
      </Typography>

      {metadata.volume == null && (
        <Chip
          label={t('cad.metadata.nonWatertightMesh')}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ mb: 1 }}
        />
      )}

      {sections.map((section, idx) => (
        <Box key={section.title} sx={{ mb: idx < sections.length - 1 ? 1.5 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            {section.icon}
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {section.title}
            </Typography>
          </Box>
          <Table size="small">
            <TableBody>
              {section.rows.map(([label, value]) => (
                <TableRow key={label as string} sx={{ '& td': { py: 0.3, border: 0 } }}>
                  <TableCell sx={{ color: 'text.secondary', pl: 0, width: '45%' }}>
                    <Typography variant="caption">{label}</Typography>
                  </TableCell>
                  <TableCell sx={{ pr: 0 }}>
                    <Typography variant="caption" fontWeight={500}>{value}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}

      {/* Wall Thickness */}
      {metadata.wallThickness && metadata.wallThickness.minMm != null && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('cad.metadata.wallThickness')}
            </Typography>
            <Chip
              label={getConfidenceLabel(metadata.wallThickness.confidence)}
              size="small"
              color={metadata.wallThickness.confidence === 'high' ? 'success' : metadata.wallThickness.confidence === 'medium' ? 'warning' : 'error'}
              variant="outlined"
              sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 } }}
            />
          </Box>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                <TableCell sx={{ color: 'text.secondary', pl: 0, width: '45%' }}><Typography variant="caption">{t('cad.metadata.min')}</Typography></TableCell>
                <TableCell sx={{ pr: 0 }}><Typography variant="caption" fontWeight={500}>{metadata.wallThickness.minMm?.toFixed(2)} mm</Typography></TableCell>
              </TableRow>
              <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                <TableCell sx={{ color: 'text.secondary', pl: 0 }}><Typography variant="caption">{t('cad.metadata.median')}</Typography></TableCell>
                <TableCell sx={{ pr: 0 }}><Typography variant="caption" fontWeight={500}>{metadata.wallThickness.medianMm?.toFixed(2)} mm</Typography></TableCell>
              </TableRow>
              <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                <TableCell sx={{ color: 'text.secondary', pl: 0 }}><Typography variant="caption">{t('cad.metadata.mean')}</Typography></TableCell>
                <TableCell sx={{ pr: 0 }}><Typography variant="caption" fontWeight={500}>{metadata.wallThickness.meanMm?.toFixed(2)} mm</Typography></TableCell>
              </TableRow>
              <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                <TableCell sx={{ color: 'text.secondary', pl: 0 }}><Typography variant="caption">{t('cad.metadata.max')}</Typography></TableCell>
                <TableCell sx={{ pr: 0 }}><Typography variant="caption" fontWeight={500}>{metadata.wallThickness.maxMm?.toFixed(2)} mm</Typography></TableCell>
              </TableRow>
              {metadata.wallThickness.hotspots.length > 0 && (
                <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                  <TableCell sx={{ color: 'text.secondary', pl: 0 }}><Typography variant="caption">{t('cad.metadata.thinAreas')}</Typography></TableCell>
                  <TableCell sx={{ pr: 0 }}><Typography variant="caption" fontWeight={500}>{t('cad.metadata.detectedCount', { count: metadata.wallThickness.hotspots.length })}</Typography></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Mesh Quality */}
      {metadata.meshQuality && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('cad.metadata.meshQuality')}
            </Typography>
          </Box>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                <TableCell sx={{ color: 'text.secondary', pl: 0, width: '45%' }}><Typography variant="caption">{t('cad.metadata.watertight')}</Typography></TableCell>
                <TableCell sx={{ pr: 0 }}><Typography variant="caption" fontWeight={500}>{metadata.meshQuality.isWatertight ? t('cad.metadata.yes') : t('cad.metadata.no')}</Typography></TableCell>
              </TableRow>
              {metadata.meshQuality.bodyCount != null && (
                <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                  <TableCell sx={{ color: 'text.secondary', pl: 0 }}><Typography variant="caption">{t('cad.metadata.bodyCount')}</Typography></TableCell>
                  <TableCell sx={{ pr: 0 }}><Typography variant="caption" fontWeight={500}>{metadata.meshQuality.bodyCount}</Typography></TableCell>
                </TableRow>
              )}
              {metadata.meshQuality.degenerateFaces != null && metadata.meshQuality.degenerateFaces > 0 && (
                <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                  <TableCell sx={{ color: 'text.secondary', pl: 0 }}><Typography variant="caption">{t('cad.metadata.degenerateFaces')}</Typography></TableCell>
                  <TableCell sx={{ pr: 0 }}><Typography variant="caption" fontWeight={500} color="warning.main">{metadata.meshQuality.degenerateFaces}</Typography></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Draft Risk */}
      {metadata.draftRisk && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('cad.metadata.draftRisk')}
            </Typography>
            <Chip
              label={getConfidenceLabel(metadata.draftRisk.confidence)}
              size="small"
              color={metadata.draftRisk.confidence === 'high' ? 'success' : metadata.draftRisk.confidence === 'medium' ? 'warning' : 'error'}
              variant="outlined"
              sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 } }}
            />
          </Box>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                <TableCell sx={{ color: 'text.secondary', pl: 0, width: '45%' }}><Typography variant="caption">{t('cad.metadata.undercutFraction')}</Typography></TableCell>
                <TableCell sx={{ pr: 0 }}>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    color={
                      metadata.draftRisk.undercutFraction * 100 > 15
                        ? 'error.main'
                        : metadata.draftRisk.undercutFraction * 100 > 5
                          ? 'warning.main'
                          : 'success.main'
                    }
                  >
                    {(metadata.draftRisk.undercutFraction * 100).toFixed(1)}%
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                <TableCell sx={{ color: 'text.secondary', pl: 0 }}><Typography variant="caption">{t('cad.metadata.riskRegionCount')}</Typography></TableCell>
                <TableCell sx={{ pr: 0 }}><Typography variant="caption" fontWeight={500}>{t('cad.metadata.count', { count: metadata.draftRisk.riskRegions.length })}</Typography></TableCell>
              </TableRow>
              <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                <TableCell sx={{ color: 'text.secondary', pl: 0 }}><Typography variant="caption">{t('cad.metadata.pullDirection')}</Typography></TableCell>
                <TableCell sx={{ pr: 0 }}>
                  <Typography variant="caption" fontWeight={500}>
                    [{metadata.draftRisk.pullDirection.map((v) => v.toFixed(2)).join(', ')}]
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Analysis Scope */}
      {metadata.geometryAnalysis?.analysisScope && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('cad.metadata.analysisScope')}
            </Typography>
          </Box>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ '& td': { py: 0.3, border: 0 } }}>
                <TableCell sx={{ color: 'text.secondary', pl: 0, width: '45%' }}><Typography variant="caption">{t('cad.metadata.target')}</Typography></TableCell>
                <TableCell sx={{ pr: 0 }}><Typography variant="caption" fontWeight={500}>{metadata.geometryAnalysis.analysisScope}</Typography></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default CADMetadataPanel;
